/**
 * useNotifications - Integrates WebSocket alerts with Toast and Browser notifications
 */
import { useEffect, useCallback, createContext, useContext, ReactNode } from 'react'
import { useAlertFeed } from './useAlertFeed'
import { useMarketStatus } from './useMarketStatus'
import { useScoreFeed } from './useScoreFeed'
import { showToast, type ToastSeverity } from '../components/ui/Toast'
import type { AlertFired } from '../lib/websocket'

interface NotificationContextValue {
  requestBrowserPermission: () => Promise<NotificationPermission>
  hasBrowserPermission: boolean
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

// Map alert severity to toast severity
function mapSeverity(severity: string): ToastSeverity {
  switch (severity) {
    case 'critical':
      return 'critical'
    case 'high':
      return 'high'
    case 'medium':
      return 'medium'
    case 'low':
      return 'low'
    default:
      return 'info'
  }
}

// Show browser notification for critical alerts
function showBrowserNotification(alert: AlertFired) {
  if (!('Notification' in window)) return
  if (Notification.permission !== 'granted') return

  const notification = new Notification(`Cipher Alert: ${alert.title}`, {
    body: alert.body,
    icon: '/apex-icon.svg',
    tag: alert.alert_id, // Prevent duplicates
    requireInteraction: alert.severity === 'critical',
    data: { alertId: alert.alert_id, ticker: alert.ticker }
  })

  notification.onclick = () => {
    window.focus()
    notification.close()
    // Navigate to alerts page or specific ticker
    if (alert.ticker) {
      window.location.href = `/watchlist?ticker=${alert.ticker}`
    } else {
      window.location.href = '/alerts'
    }
  }
}

// Listen for alerts and show notifications
function useAlertNotifications() {
  const { lastAlert: _lastAlert } = useAlertFeed()

  useEffect(() => {
    // Listen for custom alert events from the WebSocket
    const handleAlert = (event: CustomEvent<AlertFired>) => {
      const alert = event.detail

      // Show toast notification
      showToast({
        severity: mapSeverity(alert.severity),
        title: alert.title,
        body: alert.body,
        ticker: alert.ticker || undefined,
        action: alert.ticker
          ? {
              label: 'View',
              onClick: () => {
                window.location.href = `/watchlist?ticker=${alert.ticker}`
              }
            }
          : undefined
      })

      // Show browser notification for critical/high severity
      if (alert.severity === 'critical' || alert.severity === 'high') {
        showBrowserNotification(alert)
      }
    }

    window.addEventListener('apex:alert' as any, handleAlert)
    return () => window.removeEventListener('apex:alert' as any, handleAlert)
  }, [])
}

// Listen for market events and show notifications
function useMarketNotifications() {
  const { lastEvent } = useMarketStatus()

  useEffect(() => {
    if (!lastEvent) return

    const isOpen = lastEvent.type === 'market_open'

    showToast({
      severity: 'info',
      title: isOpen ? 'Market Open' : 'Market Closed',
      body: lastEvent.summary || (isOpen
        ? 'US markets are now open for trading.'
        : 'US markets have closed for the day.'),
      duration: 5000
    })
  }, [lastEvent])
}

// Provider component
interface NotificationProviderProps {
  children: ReactNode
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  // Call hooks unconditionally to follow React's rules of hooks
  // useAlertFeed already checks isAuthenticated internally
  useAlertNotifications()
  useMarketNotifications()

  // Listen for score update notifications
  useScoreFeed()

  const requestBrowserPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      return 'denied' as NotificationPermission
    }

    if (Notification.permission === 'granted') {
      return 'granted'
    }

    if (Notification.permission !== 'denied') {
      return await Notification.requestPermission()
    }

    return Notification.permission
  }, [])

  const hasBrowserPermission =
    'Notification' in window && Notification.permission === 'granted'

  return (
    <NotificationContext.Provider
      value={{ requestBrowserPermission, hasBrowserPermission }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider')
  }
  return context
}

export default useNotifications
